import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import Layout from "@/themes";
import Dashboard from "../pages/Dashboard.vue";
import NotFound from "../pages/NotFound.vue";

import { publicRoutes as userPublicRoutes, protectedRoutes as userProtectedRoutes } from "@/modules/Users/routes";
import { protectedRoutes as logsProtectedRoutes } from "@/modules/SystemLogs/routes";
import { protectedRoutes as settingsProtectedRoutes } from "@/modules/Settings/routes";
import { protectedRoutes as familiesProtectedRoutes } from "@/modules/Families/routes";
import { protectedRoutes as schoolProtectedRoutes } from "@/modules/School/routes";
import { protectedRoutes as anpaProtectedRoutes } from "@/modules/Anpa/routes";
import { protectedRoutes as feesProtectedRoutes } from "@/modules/Fees/routes";
import { protectedRoutes as documentsProtectedRoutes } from "@/modules/Documents/routes";
import { protectedRoutes as mailboxProtectedRoutes } from "@/modules/Mailbox/routes";
import { protectedRoutes as mailingsProtectedRoutes } from "@/modules/Mailings/routes";
import { protectedRoutes as managementProtectedRoutes } from "@/modules/Management/routes";
import { protectedRoutes as activitiesProtectedRoutes } from "@/modules/Activities/routes";
import {
  publicRoutes as sitePublicRoutes,
  protectedRoutes as siteProtectedRoutes,
} from "@/modules/Site/routes";
import { protectedRoutes as benefitsProtectedRoutes } from "@/modules/Benefits/routes";
import { protectedRoutes as chatProtectedRoutes } from "@/modules/Chat/routes";
import { publicRoutes as authPublicRoutes } from "@/modules/Auth/routes";

function resolveRole(authStore: ReturnType<typeof useAuthStore>): string | null {
  if (authStore.user?.role) {
    return authStore.user.role;
  }
  try {
    const raw = localStorage.getItem("user_info");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.role) {
        authStore.user = parsed;
        return parsed.role;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    // Dentro do espazo de familia: non saltar ao top ao cambiar de apartado
    if (to.meta.familyWorkspace && from.meta.familyWorkspace) {
      return false;
    }
    return { top: 0 };
  },
  routes: [
    ...sitePublicRoutes,
    {
      path: "/app",
      component: Layout,
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          name: "dashboard",
          component: Dashboard,
        },
        ...userProtectedRoutes,
        ...familiesProtectedRoutes,
        ...feesProtectedRoutes,
        ...documentsProtectedRoutes,
        ...mailboxProtectedRoutes,
        ...mailingsProtectedRoutes,
        ...chatProtectedRoutes,
        ...activitiesProtectedRoutes,
        ...benefitsProtectedRoutes,
        ...managementProtectedRoutes,
        ...siteProtectedRoutes,
        ...schoolProtectedRoutes,
        ...anpaProtectedRoutes,
        ...logsProtectedRoutes,
        ...settingsProtectedRoutes,
      ],
    },
    ...userPublicRoutes,
    ...authPublicRoutes,
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: NotFound,
    },
  ],
});

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore();
  const role = resolveRole(authStore);

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  const needsAdmin = to.matched.some((r) => r.meta.requiresAdmin);
  const needsStaff = to.matched.some((r) => r.meta.requiresStaff);

  if (needsAdmin && role !== "ADMIN") {
    next({ name: "dashboard" });
    return;
  }

  if (needsStaff && !["ADMIN", "TECNICO"].includes(role || "")) {
    next({ name: "dashboard" });
    return;
  }

  if (to.meta.guestOnly && authStore.isAuthenticated) {
    next({ name: "dashboard" });
    return;
  }

  next();
});

export default router;
